import cn from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { getUserType } from 'redux/slices/userSlice';
import { IconButton } from 'shared/ui';
import Clue from 'shared/ui/Clue';

import endingSvg from '../../../assets/img/ending.svg';
import prefixSvg from '../../../assets/img/prefix.svg';
import rootSvg from '../../../assets/img/root.svg';
import suffixSvg from '../../../assets/img/suffix.svg';
import wordPrefixSvg from '../../../assets/img/word-prefix.svg';
import wordRootSvg from '../../../assets/img/word-root.svg';
import wordSuffixSvg from '../../../assets/img/word-suffix.svg';
import {
  ICL,
  WordLettersToWork,
  TaskKeysType,
  WordCompositionTask,
  WordPart,
  WordLettersToShow,
} from '../../../shared/types';
import StudentTaskWrapper from '../StudentTaskWrapper';

interface StudentWordCompositionTaskProps extends WordCompositionTask {
  icl: ICL | null;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: any) => void;
  saveTaskAnswer: (id: string) => Promise<void>;
  hw?: boolean;
  preview?: boolean;
  studentName?: string;
  index: number;
}

export default function StudentWordCompositionTask({
  icl,
  text,
  subText,
  answer,
  hasAnswer,
  hasStar,
  checkedAt,
  completedAt,
  handleChangeTaskByKey,
  id,
  hasPhoto,
  answersPhotos,
  saveTaskAnswer,
  deadline,
  audioUrl,
  videoUrl,
  taskPhotos,
  editedAnswerPhotos,
  hw,
  pdfUrl,
  wordUrl,
  studentPdfUrl,
  studentWordUrl,
  preview,
  words,
  studentName,
  index,
}: StudentWordCompositionTaskProps) {
  const userType = useSelector(getUserType);
  const [wordPart, setWordPart] = useState<WordPart | ''>('');

  const getBgImg = () => {
    switch (wordPart) {
      case WordPart.prefix:
        return wordPrefixSvg;

      case WordPart.root:
        return wordRootSvg;

      case WordPart.suffix:
        return wordSuffixSvg;

      case WordPart.ending:
        return WordPart.ending;

      default:
        return '';
    }
  };

  const getWordParts = (wordLettersToWork: WordLettersToWork[]) => {
    const svgsOfWordParts = [wordPrefixSvg, wordRootSvg, wordSuffixSvg, WordPart.ending];
    const wordParts: WordLettersToShow[] = [];
    let numberOfLettersToShow = 0;

    svgsOfWordParts.forEach(svg => {
      const lettersOfWordPart = wordLettersToWork
        .filter(wordLetter => wordLetter.bgImg === svg)
        .map(wordLetter => wordLetter.letter)
        .join('');

      if (lettersOfWordPart.length) {
        const wordPart: WordLettersToShow = { id: uuidv4(), letters: lettersOfWordPart, bgImg: svg };

        numberOfLettersToShow += lettersOfWordPart.length;
        wordParts.push(wordPart);
      }
    });

    return { wordParts, numberOfLettersToShow };
  };

  const changeFormikWord = (wordId: string, letterId: string) => {
    const changedWords = words.map(word => {
      if (word.id === wordId) {
        const changedWordLettersToWork = word.wordLettersToWork.map(wordLetter =>
          wordLetter.id === letterId ? ({ ...wordLetter, bgImg: getBgImg() } as WordLettersToWork) : wordLetter,
        );

        const changedWordLettersToShow = getWordParts(changedWordLettersToWork);

        return {
          ...word,
          wordLettersToWork: changedWordLettersToWork,
          wordLettersToShow: changedWordLettersToShow.wordParts,
          numberOfLettersToShow: changedWordLettersToShow.numberOfLettersToShow,
        };
      }

      return word;
    });

    handleChangeTaskByKey(id, 'words', changedWords);
  };

  const areStudentAnswers = words.every(word => word.wordLettersToWork.length === word.numberOfLettersToShow);
  const isDisabledSubmitButton = !areStudentAnswers || !!checkedAt || !!completedAt;

  const isStudent = userType === 'Student';
  const isButtonDisabled = !!checkedAt || !!completedAt || !isStudent || !!preview;

  return (
    <StudentTaskWrapper
      icl={icl}
      taskText={text}
      answersPhotos={answersPhotos}
      id={id}
      handleChangeTaskByKey={handleChangeTaskByKey}
      checkedAt={checkedAt}
      hasPhoto={hasPhoto}
      completedAt={completedAt}
      saveTaskAnswer={saveTaskAnswer}
      deadline={deadline}
      audioUrl={audioUrl}
      answer={answer}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      videoUrl={videoUrl}
      taskPhotos={taskPhotos}
      editedAnswerPhotos={editedAnswerPhotos}
      hw={hw}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      preview={preview}
      subText={subText}
      isDisabledSubmitButton={isDisabledSubmitButton}
      studentName={studentName}
      isSubmitButton
      index={index}
    >
      <div className="!py-5 md:!py-8">
        <Clue text="studentWordCompositionHint" className="!mb-5 md:!mb-6" />

        <div className="!mb-3 grid !gap-3 md:grid-cols-2">
          {words.map(word => (
            <div className="flex !gap-4" key={word.id}>
              <ul className="flex !gap-1">
                {word.wordLettersToWork.map(wordLetter => (
                  <li key={wordLetter.id}>
                    <button
                      onClick={() => changeFormikWord(word.id, wordLetter.id)}
                      className={cn(
                        'text-lg font-medium',
                        wordLetter.bgImg === WordPart.ending ? 'border-2 border-gray-600' : '',
                      )}
                      style={{
                        background: wordLetter.bgImg ? `url(${wordLetter.bgImg})` : '',
                      }}
                      disabled={isButtonDisabled}
                    >
                      {wordLetter.letter}
                    </button>
                  </li>
                ))}
              </ul>

              <ul className="flex">
                {word.wordLettersToShow?.map(wordLetter => (
                  <li key={wordLetter.id}>
                    <p
                      style={{
                        background: wordLetter.bgImg ? `url(${wordLetter.bgImg})` : '',
                        backgroundSize: '100% 100%',
                      }}
                      className={cn(
                        '!mb-0 text-lg font-medium',
                        wordLetter.bgImg === WordPart.ending && 'border-2 border-gray-600',
                      )}
                    >
                      {wordLetter.letters}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex !gap-3">
          <IconButton
            title="prefix"
            icon={prefixSvg}
            onClick={() => setWordPart(WordPart.prefix)}
            disabled={isButtonDisabled}
          />
          <IconButton
            title="root"
            icon={rootSvg}
            onClick={() => setWordPart(WordPart.root)}
            disabled={isButtonDisabled}
          />
          <IconButton
            title="suffix"
            icon={suffixSvg}
            onClick={() => setWordPart(WordPart.suffix)}
            disabled={isButtonDisabled}
          />
          <IconButton
            title="ending"
            icon={endingSvg}
            onClick={() => setWordPart(WordPart.ending)}
            disabled={isButtonDisabled}
          />
        </div>
      </div>
    </StudentTaskWrapper>
  );
}
